import { studyMetadataManager } from '@ohif/core/src/utils';
import { ProtocolStore } from '@ohif/core/src/hanging-protocols/protocolStore/classes';
import hangingProtocols from '@ohif/core/src/hanging-protocols';
import Protocol from '@ohif/core/src/hanging-protocols/classes/Protocol';
import { StudyMetadataSource as LocalStudyMetadataSource } from './LocalStudyMetadataSource';
import store from '../store/store';
import { getStudy } from '../util/elementsUtils';
import { findStudySeries } from '../util/studiesManager';
import { createLayoutElement } from '../components/App/Viewer/Layout/LayoutUtils';
import { getAvailableRenderers } from '../model/getAvailableRenderers';
import { setMatchedProtocols } from '../actions/protocolActions';

class MinimalStudyMetadataSource extends LocalStudyMetadataSource {
  getByInstanceUID() { return Promise.resolve(null); }
  loadStudy() { return Promise.resolve(null); }
}

const studyMetadataSource = new MinimalStudyMetadataSource();

const getActiveStudyMetadata = (patient) => {
  if (!patient || !Array.isArray(patient.studies) || patient.studies.length === 0) {
    return null;
  }

  const activeStudyId = patient.mainStudyId || patient.studies[0].StudyInstanceUID;

  return studyMetadataManager.get(activeStudyId) || null;
};

const evaluateConstraint = (actualValue, constraint) => {
  if (!constraint || actualValue === undefined || actualValue === null) return false;

  if (constraint.equals !== undefined) {
    const expected = constraint.equals?.value !== undefined
      ? constraint.equals.value
      : constraint.equals;
    return String(actualValue) === String(expected);
  }
  if (constraint.doesNotEqual !== undefined) {
    const expected = constraint.doesNotEqual?.value !== undefined
      ? constraint.doesNotEqual.value
      : constraint.doesNotEqual;
    return String(actualValue) !== String(expected);
  }
  if (constraint.contains !== undefined) {
    const expected = constraint.contains?.value !== undefined
      ? constraint.contains.value
      : constraint.contains;
    return String(actualValue).includes(String(expected));
  }
  if (constraint.doesNotContain !== undefined) {
    const expected = constraint.doesNotContain?.value !== undefined
      ? constraint.doesNotContain.value
      : constraint.doesNotContain;
    return !String(actualValue).includes(String(expected));
  }
  if (constraint.startsWith !== undefined) {
    const expected = constraint.startsWith?.value !== undefined
      ? constraint.startsWith.value
      : constraint.startsWith;
    return String(actualValue).startsWith(String(expected));
  }
  if (constraint.greaterThan !== undefined) {
    const expected = constraint.greaterThan?.value !== undefined
      ? constraint.greaterThan.value
      : constraint.greaterThan;
    return Number(actualValue) > Number(expected);
  }
  if (constraint.lessThan !== undefined) {
    const expected = constraint.lessThan?.value !== undefined
      ? constraint.lessThan.value
      : constraint.lessThan;
    return Number(actualValue) < Number(expected);
  }

  console.warn('[HP Matcher] Constraint desconocido:', constraint);
  return false;
};

const resolveAttributeValue = (instance, attribute) => {
  if (!instance) return undefined;
  if (typeof instance.getTagValue === 'function') {
    try {
      const val = instance.getTagValue(attribute);
      if (val !== undefined && val !== null) return val;
    } catch (e) {
      return undefined;
    }
  }

  if (typeof instance.getData === 'function') {
    const data = instance.getData();
    if (data) {
      if (data[attribute] !== undefined && data[attribute] !== null) return data[attribute];
      if (data.metadata && data.metadata[attribute] !== undefined && data.metadata[attribute] !== null) {
        return data.metadata[attribute];
      }
    }
  }

  if (instance[attribute] !== undefined && instance[attribute] !== null) return instance[attribute];

  if (instance._data) {
    const data = instance._data;
    if (data[attribute] !== undefined && data[attribute] !== null) return data[attribute];
    if (data.metadata && data.metadata[attribute] !== undefined && data.metadata[attribute] !== null) {
      return data.metadata[attribute];
    }
  }

  if (typeof instance.getFirstInstance === 'function') {
    const firstInstance = instance.getFirstInstance();
    if (firstInstance && firstInstance !== instance) {
      return resolveAttributeValue(firstInstance, attribute);
    }
  }

  return undefined;
};

export const isMGStudy = (studyMetadata) => {
  if (!studyMetadata || typeof studyMetadata.getSeries !== 'function') {
    return false;
  }

  return studyMetadata
    .getSeries()
    .some((series) => String(resolveAttributeValue(series, 'Modality')).toUpperCase() === 'MG');
};

const evaluateRules = (instance, rules) => {
  if (!rules || rules.length === 0) return 0;
  let score = 0;
  for (const rule of rules) {
    const value = resolveAttributeValue(instance, rule.attribute);
    const passed = evaluateConstraint(value, rule.constraint);
    if (rule.required && !passed) return -1; // regla required fallida → descalifica
    if (passed) score += (rule.weight || 1);
  }
  return score;
};

const findBestProtocol = (protocolDefinitions, studyMetadata) => {
  const firstInstance = studyMetadata.getFirstInstance();

  const matchedProtocols = [];

  for (const protoDef of protocolDefinitions) {
    const rules = protoDef.protocolMatchingRules || [];
    if (rules.length === 0) continue;

    const score = evaluateRules(firstInstance, rules);

    if (score > 0) {
      matchedProtocols.push({ ...protoDef, score });
    }
  }

  // Store all matched protocols in Redux state
  store.dispatch(setMatchedProtocols(matchedProtocols));

  if (matchedProtocols.length === 0) {
    return { bestProtocol: null, bestScore: 0 };
  }

  // From the matched protocols, find the best one
  matchedProtocols.sort((a, b) => b.score - a.score);
  let bestProtocols = matchedProtocols.filter(p => p.score === matchedProtocols[0].score);

  if (bestProtocols.length > 1) {
    const { DEFAULT_MAMMO_PROTOCOL } = store.getState().config;
    if (DEFAULT_MAMMO_PROTOCOL) {
      const defaultProtocol = bestProtocols.find(p => p.id === DEFAULT_MAMMO_PROTOCOL);
      if (defaultProtocol) {
        return { bestProtocol: defaultProtocol, bestScore: defaultProtocol.score };
      }
    }
  }

  const bestProtocol = bestProtocols[0];
  return { bestProtocol, bestScore: bestProtocol.score };
};

export const applyBestHangingProtocol = (patient) => {
  try {
    const activeStudyMetadata = getActiveStudyMetadata(patient);
    if (!activeStudyMetadata || !isMGStudy(activeStudyMetadata)) {
      store.dispatch(setMatchedProtocols([]));
      return null;
    }

    const studyMetadataList = patient.studies
      .map(study => studyMetadataManager.get(study.StudyInstanceUID))
      .filter(Boolean);

    const activeStudyIndex = studyMetadataList.indexOf(activeStudyMetadata);
    if (activeStudyIndex === -1) {
      studyMetadataList.unshift(activeStudyMetadata);
    } else if (activeStudyIndex > 0) {
      studyMetadataList.splice(activeStudyIndex, 1);
      studyMetadataList.unshift(activeStudyMetadata);
    }

    const protocolDefinitions = store.getState().config?.hangingProtocols || [];
    if (protocolDefinitions.length === 0) {
      store.dispatch(setMatchedProtocols([]));
      return null;
    }

    const { ProtocolStrategy } = hangingProtocols;
    const strategy = new ProtocolStrategy();
    strategy.onReady(() => {});

    protocolDefinitions.forEach(p => {
      const protocolInstance = new Protocol();
      protocolInstance.fromObject(p);
      strategy.addProtocol(protocolInstance);
    });

    const protocolStore = new ProtocolStore(strategy);

    Array.from(protocolStore.getProtocol()).map((p) => p.id || p.name);

    const matchedViewports = [];
    let matchedLayout = null;

    const options = {
      setLayout: ({ numRows, numColumns }) => {
        matchedLayout = { rows: numRows, columns: numColumns };
        },
      setViewportSpecificData: (viewportIndex, data) => {
        matchedViewports[viewportIndex] = data;
        },
    };

    const { bestProtocol } = findBestProtocol(protocolDefinitions, activeStudyMetadata);

    const engine = new hangingProtocols.ProtocolEngine(
      protocolStore,
      studyMetadataList,
      new Map(),
      studyMetadataSource,
      options
    );

    if (bestProtocol) {
      const winnerInstance = Array.from(protocolStore.getProtocol())
        .find(pr => pr.id === bestProtocol.id);

      if (winnerInstance) {
        matchedLayout = null;
        matchedViewports.length = 0;
        engine.setHangingProtocol(winnerInstance);
      }
    }

    if (matchedLayout && matchedViewports.length > 0) {
      const newElements = matchedViewports.map(vpData => {
        if (!vpData || !vpData.SeriesInstanceUID) return {};

        const study = getStudy(patient, vpData.StudyInstanceUID);
        const series = findStudySeries({ study, SeriesInstanceUID: vpData.SeriesInstanceUID });

        if (!series) return {};

        const availableRenderers = getAvailableRenderers(series.instances || []);
        const renderType = availableRenderers[0] || null;

        return createLayoutElement(
          vpData.StudyInstanceUID,
          vpData.SeriesInstanceUID,
          renderType,
          {},
          availableRenderers
        );
      });

      return {
        layout: matchedLayout,
        elements: newElements
      };
    }

  } catch (error) {
    console.error('Error ejecutando Hanging Protocols:', error);
  }
  return null;
};

export const applyHangingProtocolById = (patient, protocolId) => {
  try {
    const activeStudyMetadata = getActiveStudyMetadata(patient);
    if (!activeStudyMetadata || !isMGStudy(activeStudyMetadata)) {
      store.dispatch(setMatchedProtocols([]));
      return null;
    }

    const applicableProtocolIds = store.getState().protocols?.matchedProtocols
      .map(protocol => protocol.id) || [];
    if (!applicableProtocolIds.includes(protocolId)) {
      return null;
    }

    const studyMetadataList = patient.studies
      .map(study => studyMetadataManager.get(study.StudyInstanceUID))
      .filter(Boolean);

    const activeStudyIndex = studyMetadataList.indexOf(activeStudyMetadata);
    if (activeStudyIndex === -1) {
      studyMetadataList.unshift(activeStudyMetadata);
    } else if (activeStudyIndex > 0) {
      studyMetadataList.splice(activeStudyIndex, 1);
      studyMetadataList.unshift(activeStudyMetadata);
    }

    const protocolDefinitions = store.getState().config?.hangingProtocols || [];

    const { ProtocolStrategy } = hangingProtocols;
    const strategy = new ProtocolStrategy();
    strategy.onReady(() => {});

    protocolDefinitions.forEach(p => {
      const protocolInstance = new Protocol();
      protocolInstance.fromObject(p);
      strategy.addProtocol(protocolInstance);
    });

    const protocolStore = new ProtocolStore(strategy);

    let matchedLayout = null;
    const matchedViewports = [];

    const options = {
      setLayout: ({ numRows, numColumns }) => {
        matchedLayout = { rows: numRows, columns: numColumns };
      },
      setViewportSpecificData: (viewportIndex, data) => {
        matchedViewports[viewportIndex] = data;
      },
    };

    const engine = new hangingProtocols.ProtocolEngine(
      protocolStore,
      studyMetadataList,
      new Map(),
      studyMetadataSource,
      options
    );

    const winnerInstance = Array.from(protocolStore.getProtocol())
      .find(pr => pr.id === protocolId);

    if (winnerInstance) {
      engine.setHangingProtocol(winnerInstance);
    } else {
      return null;
    }

    if (matchedLayout && matchedViewports.length > 0) {
      const newElements = matchedViewports.map(vpData => {
        if (!vpData || !vpData.SeriesInstanceUID) return {};

        const study = getStudy(patient, vpData.StudyInstanceUID);
        const series = findStudySeries({ study, SeriesInstanceUID: vpData.SeriesInstanceUID });

        if (!series) return {};

        const availableRenderers = getAvailableRenderers(series.instances || []);
        const renderType = availableRenderers[0] || null;

        return createLayoutElement(
          vpData.StudyInstanceUID,
          vpData.SeriesInstanceUID,
          renderType,
          {},
          availableRenderers
        );
      });

      return {
        layout: matchedLayout,
        elements: newElements
      };
    }
  } catch (error) {
    console.error('Error applying specific protocol:', error);
  }
  return null;
};
